import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';
import { Spinner } from '../spinner/spinner.tsx';

export const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 font-bold whitespace-nowrap select-none transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint disabled:opacity-40 disabled:pointer-events-none aria-disabled:opacity-40',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-white hover:bg-primary-hover',
        accent:
          'bg-accent text-on-accent hover:-translate-y-px hover:shadow-glow disabled:translate-y-0 disabled:shadow-none',
        quiet: 'bg-fill text-ink hover:bg-line',
        ghost: 'bg-transparent text-ink-secondary hover:bg-fill hover:text-ink',
        danger: 'bg-danger text-on-danger hover:bg-danger-hover',
        /*
         * Destructive, but not the loudest thing on the screen.
         *
         * `danger` is for a destructive action that is the point of the screen —
         * the confirm button in a delete dialog. This is for one that merely
         * lives on a row among others, like disconnecting a calendar: still
         * unmistakably destructive by colour, without shouting over the content
         * it sits beside. Shopify's account-connection pattern uses exactly this
         * pairing — a secondary button carrying a critical tone.
         */
        dangerQuiet: 'bg-danger-fill text-danger hover:bg-danger-line',
        /*
         * For a control sitting on navy — the gesprekstype on a meeting's hero
         * bar. The light-surface variants all read their contrast from a white
         * background and go invisible here, so this one is white-alpha instead:
         * a translucent fill with a hairline, the same relationship `quiet` has
         * to a white page.
         */
        onPrimary:
          'border border-on-primary-line bg-on-primary-fill text-white hover:bg-on-primary-fill-hover',
        /*
         * A button that reads as a link, for an action that continues a
         * sentence — "Lees meer" under a clipped paragraph. It carries no fill
         * and no box: a pill there would read as the block's own action and
         * pull rank over the one at the foot of the card.
         */
        link: 'bg-transparent text-ink-secondary underline underline-offset-2 hover:text-ink',
      },
      shape: {
        pill: 'rounded-full',
        block: 'rounded-md',
      },
      size: {
        sm: 'text-sm px-3.5 py-2',
        md: 'text-base px-4.5 py-2.5',
        lg: 'text-base px-5 py-3',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    /*
     * Accent is ALWAYS block: color encodes hierarchy, shape encodes context.
     * The compound class lands after the shape class, so tailwind-merge (cn)
     * resolves the rounded-* conflict in its favor — a green pill is inexpressible.
     */
    compoundVariants: [
      { variant: 'accent', className: 'rounded-md' },
      /*
       * Same trick as accent's shape: the compound class lands after the size
       * class, so tailwind-merge drops the padding a link must not have
       * whichever size it is asked for.
       */
      { variant: 'link', className: 'p-0' },
    ],
    defaultVariants: {
      variant: 'primary',
      shape: 'pill',
      size: 'md',
      fullWidth: false,
    },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

export interface ButtonProps
  extends Omit<React.ComponentPropsWithRef<'button'>, 'style'>, ButtonVariants {
  loading?: boolean;
}

export function Button({
  className,
  variant,
  shape,
  size,
  fullWidth,
  loading = false,
  disabled,
  type = 'button',
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      data-slot='button'
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, shape, size, fullWidth }), className)}
      {...props}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}
